import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Palette, Image as ImageIcon } from 'lucide-react';
import { COLOR_PRESETS } from '../../data/createClubData';
import ImageUploadInput from '../ImageUploadInput';

export default function Step4Branding({ formData, setFormData }) {
  return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Club Branding
              </CardTitle>
              <CardDescription>Choose your club's color scheme</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Color Presets */}
              <div>
                <Label className="mb-3 block">Color Presets</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {COLOR_PRESETS.map(preset => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => selectColorPreset(preset)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        formData.selectedPreset === preset.name 
                          ? 'ring-2 ring-primary border-primary' 
                          : 'hover:border-primary/50'
                      }`}
                      data-testid={`color-preset-${preset.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <div className="flex gap-1 mb-2">
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.primary }} />
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.secondary }} />
                        <div className="w-6 h-6 rounded border" style={{ backgroundColor: preset.accent }} />
                      </div>
                      <div className="text-sm font-medium">{preset.name}</div>
                      <div className="text-xs text-muted-foreground">{preset.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Colors */}
              <div>
                <Label className="mb-3 block">Or Customize</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="primary" className="text-xs">Primary</Label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        id="primary"
                        value={formData.primary_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={formData.primary_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="flex-1 text-xs"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="secondary" className="text-xs">Secondary</Label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        id="secondary"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, secondary_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={formData.secondary_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, secondary_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="flex-1 text-xs"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="accent" className="text-xs">Accent</Label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        id="accent"
                        value={formData.accent_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, accent_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={formData.accent_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, accent_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="flex-1 text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div>
                <Label className="mb-3 block">Preview</Label>
                <div 
                  className="rounded-lg p-6 text-white"
                  style={{ backgroundColor: formData.primary_color }}
                >
                  <h3 className="font-heading text-xl mb-2">{formData.name || 'Your Club Name'}</h3>
                  <p className="text-sm opacity-90 mb-4">{formData.tagline || 'Your tagline here'}</p>
                  <div className="flex gap-2">
                    <button 
                      className="px-4 py-2 rounded text-sm font-medium"
                      style={{ backgroundColor: formData.secondary_color }}
                    >
                      Join Club
                    </button>
                    <button 
                      className="px-4 py-2 rounded text-sm font-medium"
                      style={{ backgroundColor: formData.accent_color, color: formData.primary_color }}
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              </div>

              {/* Logo & Banner (optional at creation — can be added later in Club Admin) */}
              <div className="border-t pt-6">
                <div className="mb-3 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                  <Label className="mb-0">Logo & Banner <span className="text-xs text-muted-foreground font-normal">(optional — can be added later)</span></Label>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <ImageUploadInput
                    label="Logo"
                    value={formData.logo_url || ''}
                    onChange={(url) => setFormData(prev => ({ ...prev, logo_url: url }))}
                    kind="logo"
                    placeholder="https://... or Upload"
                    testId="createclub-logo"
                    previewClassName="h-20 max-w-full object-contain rounded border bg-muted/30 p-1"
                  />
                  <ImageUploadInput
                    label="Banner"
                    value={formData.banner_url || ''}
                    onChange={(url) => setFormData(prev => ({ ...prev, banner_url: url }))}
                    kind="banner"
                    placeholder="https://... or Upload"
                    testId="createclub-banner"
                    previewClassName="h-24 w-full object-cover rounded border"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
  );
}
